import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, ArrowRight, ArrowLeft, Check, ExternalLink } from 'lucide-react';

// Contextual step definitions keyed by workflow. Wording is sourced from the
// existing CardProgrammingGuide (#rooms) — this component only SURFACES it, it
// is NOT a second source of truth. Add 'building'/'unit' here in Phase 2.
const FLOWS = {
    room: { steps: ['room_s1', 'room_s2', 'room_s3', 'room_s4', 'room_s5'], guideHash: '#rooms', offer: 'offerRoom', openGuide: 'openGuide' },
    unit: { steps: ['unit_s1', 'unit_s2', 'unit_s3', 'unit_s4', 'unit_s5'], guideHash: '#units', offer: 'offerUnit', openGuide: 'openGuideUnit' },
};

/**
 * A small, dismissible, contextual walkthrough offered EVERY time a setup
 * workflow starts (no localStorage dismissal). Guidance-only: it never blocks
 * or resets the underlying form.
 *
 * Props: open, onClose, flow ('room'), onDismiss (optional analytics).
 */
export function SetupWalkthrough({ open, onClose, flow = 'room' }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState('offer'); // 'offer' | 'steps'
    const [i, setI] = useState(0);
    const cfg = FLOWS[flow] || FLOWS.room;
    const steps = cfg.steps;

    const reset = () => { setMode('offer'); setI(0); };
    const close = () => { reset(); onClose?.(); };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
            <DialogContent className="max-w-md" data-testid="setup-walkthrough">
                <DialogTitle className="sr-only">{t('roomSetup.offerTitle')}</DialogTitle>
                <DialogDescription className="sr-only">{t(`roomSetup.${cfg.offer}`)}</DialogDescription>
                {mode === 'offer' ? (
                    <div className="space-y-5 text-center" data-testid="setup-walkthrough-offer">
                        <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-amber-500" />
                        </div>
                        <div className="space-y-1.5">
                            <h2 className="text-lg font-bold text-foreground">{t('roomSetup.offerTitle')}</h2>
                            <p className="text-sm text-muted-foreground">{t(`roomSetup.${cfg.offer}`)}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                            <Button onClick={() => setMode('steps')} data-testid="setup-walk-me" className="w-full">
                                <Sparkles className="w-4 h-4 mr-2" />{t('roomSetup.walkMe')}
                            </Button>
                            <Button variant="ghost" onClick={close} data-testid="setup-do-myself" className="w-full">
                                {t('roomSetup.myself')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="setup-walkthrough-steps">
                        <p className="text-xs font-semibold uppercase tracking-wider text-amber-500">
                            {t('roomSetup.stepOf', { n: i + 1, total: steps.length })}
                        </p>
                        <h2 className="text-xl font-bold text-foreground" data-testid="setup-step-title">
                            {t(`roomSetup.${steps[i]}_t`)}
                        </h2>
                        <p className="text-sm text-muted-foreground leading-relaxed" data-testid="setup-step-body">
                            {t(`roomSetup.${steps[i]}_b`)}
                        </p>
                        <div className="pt-1 border-t border-border">
                            <p className="text-xs text-muted-foreground mt-2">{t('roomSetup.moreDetail')}</p>
                            <Link
                                to={`/card-guide${cfg.guideHash}`}
                                className="text-xs font-semibold text-amber-600 dark:text-amber-400 inline-flex items-center gap-1 hover:underline"
                                data-testid="setup-open-guide"
                            >
                                <ExternalLink className="w-3 h-3" />{t(`roomSetup.${cfg.openGuide || 'openGuide'}`)}
                            </Link>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                            <Button
                                variant="ghost" size="sm"
                                onClick={() => (i === 0 ? setMode('offer') : setI(i - 1))}
                                data-testid="setup-back"
                            >
                                <ArrowLeft className="w-4 h-4 mr-1" />{t('roomSetup.back')}
                            </Button>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={close} data-testid="setup-skip">
                                    {t('roomSetup.skip')}
                                </Button>
                                {i < steps.length - 1 ? (
                                    <Button size="sm" onClick={() => setI(i + 1)} data-testid="setup-next">
                                        {t('roomSetup.next')}<ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button size="sm" onClick={close} data-testid="setup-done">
                                        <Check className="w-4 h-4 mr-1" />{t('roomSetup.done')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default SetupWalkthrough;
